import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, TrendingUp, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { getCategoryName, getRegionName } from '../utils/labels';

function NewsCard({ news, featured = false }) {
  const getCategoryColor = (category) => {
    const colors = {
      world: 'bg-blue-500',
      politics: 'bg-purple-500',
      economy: 'bg-green-500',
      finance: 'bg-emerald-500',
      technology: 'bg-indigo-500',
      science: 'bg-cyan-500',
      health: 'bg-red-500',
      climate: 'bg-teal-500',
      sports: 'bg-orange-500',
      entertainment: 'bg-pink-500',
      gaming: 'bg-violet-500',
      auto: 'bg-gray-500',
      space: 'bg-slate-700'
    };
    return colors[category] || 'bg-gray-500';
  };

  return (
    <Link
      to={`/news/${news.id}`}
      className={`group block bg-white dark:bg-[#121212] rounded-2xl border border-black/10 dark:border-white/10 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${
        featured ? 'md:col-span-2' : ''
      }`}
      data-testid={`news-card-${news.id}`}
    >
      {news.image_url && (
        <div className="relative h-48 overflow-hidden">
          <img
            src={news.image_url}
            alt={news.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          {news.is_breaking && (
            <div className="absolute top-4 left-4 flex items-center space-x-1 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse-slow" />
              <span>BREAKING</span>
            </div>
          )}
        </div>
      )}

      <div className="p-5">
        <div className="flex items-center space-x-2 mb-3">
          <span className={`${getCategoryColor(news.category)} text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider`}>
            {getCategoryName(news.category)}
          </span>
          {news.is_trending && (
            <span className="flex items-center text-orange-500 text-xs font-medium">
              <TrendingUp className="h-3 w-3 mr-1" />
              Trending
            </span>
          )}
        </div>

        <h3 className={`font-bold text-[#09090B] dark:text-[#FAFAFA] mb-2 line-clamp-2 transition-colors ${
          featured ? 'text-2xl sm:text-3xl' : 'text-xl'
        }`}>
          {news.title}
        </h3>

        <p className="text-[#52525B] dark:text-[#A1A1AA] mb-4 line-clamp-3 leading-relaxed">
          {news.summary}
        </p>

        <div className="flex items-center justify-between text-xs text-[#52525B] dark:text-[#A1A1AA]">
          <div className="flex items-center space-x-4">
            <span className="flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              {formatDistanceToNow(new Date(news.published_at), { addSuffix: true, locale: de })}
            </span>
            <span>{news.read_time}s Lesezeit</span>
          </div>
          {news.region && (
            <span className="font-medium">{getRegionName(news.region)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default NewsCard;
